import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, ACCESS_CHANGED_EVENT } from '../lib/api';

/**
 * App-wide subscription access state from /api/billing/access-state:
 *   active           — Shopify confirmed an approved subscription (or comp /
 *                      reviewer): the app renders.
 *   no_plan_selected — never accepted a charge → ChoosePlanScreen on EVERY
 *                      route (deep links and every sidebar item included).
 *   blocked          — cancelled / frozen / declined / expired / pending →
 *                      the reactivate screen on every route.
 *   unavailable      — the server could not confirm the subscription and
 *                      the shop is outside its verified-active grace →
 *                      "try again" screen (never the dashboard).
 *
 * FAILS CLOSED. Until the first answer arrives the shell shows a neutral
 * loading state — the dashboard never flashes before access resolves. A
 * fetch error is `unavailable` (retried with backoff), not `active`.
 */

const RETRY_MS = [3000, 6000, 12000, 30000];
const POLL_MS = 5 * 60 * 1000;

export const INITIAL_ACCESS = {
  state: null,
  allowed: false,
  reason: null,
  retryable: false,
  graceEndsAt: null,
  graceReason: null,
  contentState: null,
  contentRestore: null,
  verifiedAt: null,
  message: null,
  loading: true,
  error: null,
};

function normalise(d) {
  const state = d.state || (d.allowed ? 'active' : 'unavailable');
  return {
    state,
    allowed: d.allowed === true || state === 'active',
    reason: d.reason || null,
    retryable: d.retryable === true,
    graceEndsAt: d.graceEndsAt || null,
    graceReason: d.graceReason || null,
    contentState: d.contentState || null,
    // Post-reactivation restore job ({ status, startedAt, progress, … }) — drives the "Restoring your pages…" banner.
    contentRestore: d.contentRestore || null,
    verifiedAt: d.verifiedAt || null,
    message: d.message || null,
    loading: false,
    error: null,
  };
}

export default function useAccess(shop) {
  const [access, setAccess] = useState(INITIAL_ACCESS);
  const retryRef = useRef({ attempt: 0, timer: null });
  const inflightRef = useRef(null);

  const load = useCallback(({ verify = false } = {}) => {
    if (!shop) return Promise.resolve();
    if (inflightRef.current) return inflightRef.current;
    const url = `/api/billing/access-state${verify ? '?verify=1' : ''}`;
    const run = apiFetch(url, { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 401) {
          // No usable session (dead OAuth record, token refused): not a
          // billing state — the session banner owns reconnect/reinstall.
          retryRef.current.attempt = 0;
          setAccess({ ...INITIAL_ACCESS, state: 'unauthenticated', loading: false });
          return;
        }
        let d = null;
        try { d = await r.json(); } catch { d = null; }
        if (!r.ok || !d) throw new Error(`access-state ${r.status}`);
        retryRef.current.attempt = 0;
        setAccess(normalise(d));
      })
      .catch((err) => {
        // Fail CLOSED: an unanswered check is "unavailable" (retried).
        const n = retryRef.current.attempt;
        retryRef.current.attempt = Math.min(n + 1, RETRY_MS.length - 1);
        setAccess((a) => ({
          ...a,
          state: a.state === 'active' && a.verifiedAt ? a.state : 'unavailable',
          allowed: a.state === 'active' && !!a.verifiedAt,
          reason: a.state === 'active' && a.verifiedAt ? a.reason : 'access_check_unavailable',
          retryable: true,
          loading: false,
          error: err?.message || 'access check failed',
        }));
        clearTimeout(retryRef.current.timer);
        retryRef.current.timer = setTimeout(() => load(), RETRY_MS[n] || RETRY_MS[RETRY_MS.length - 1]);
      })
      .finally(() => { inflightRef.current = null; });
    inflightRef.current = run;
    return run;
  }, [shop]);

  useEffect(() => {
    if (!shop) return undefined;
    const retry = retryRef.current;
    setAccess(INITIAL_ACCESS);
    load();
    const timer = setInterval(() => load(), POLL_MS);
    // Any API call that 402s means the state changed under us (billing
    // lapsed, plan never picked) — re-poll immediately (debounced) so the
    // shell shows the right screen now, not up to 5 minutes later.
    let debounce = null;
    const onAccessChanged = () => {
      if (debounce) return;
      debounce = setTimeout(() => { debounce = null; load(); }, 500);
    };
    window.addEventListener(ACCESS_CHANGED_EVENT, onAccessChanged);
    return () => {
      clearInterval(timer);
      if (debounce) clearTimeout(debounce);
      clearTimeout(retry.timer);
      window.removeEventListener(ACCESS_CHANGED_EVENT, onAccessChanged);
    };
  }, [shop, load]);

  return {
    ...access,
    // Force a fresh Shopify read (post-approval return, "Check again").
    refresh: (opts) => load({ verify: true, ...(opts || {}) }),
    // Cheap re-read (no Shopify round-trip) — the restore banner polls with it.
    reload: () => load(),
    active: access.state === 'active',
    blocked: access.state === 'blocked',
    unavailable: access.state === 'unavailable',
    // Fresh install that never accepted a charge → full-page plan picker.
    noPlan: access.state === 'no_plan_selected',
    // Content grace (storefront content still live while the merchant has
    // no app access) — informational only.
    grace: access.contentState === 'grace',
  };
}
